// import { useSelector } from "react-redux";

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { getCaseId, getClientId } from "../../Utils/helper";
import { shouldPlayIntro } from "../../Utils/introAnimations";
import { useCaseOpening } from "../../Providers/ProtectedRoute";
import StatusModal from "../Navbars/status-modal/status-modal";
import "./case-stage.css";

// Wait for the tab-fitting pass (it re-runs at 150 ms) so only tabs that are actually shown cycle.
const CYCLE_START_DELAY_MS = 300;
// Average time per hop, so a case with few stages spins briefly instead of crawling, capped at
// CYCLE_MAX_MS so a case with many stages doesn't drag on.
const CYCLE_HOP_MS = 200;
const CYCLE_MAX_MS = 2200;
// Added by the tab-fitting pass (setStageLabelsWidth); kept while the intro repaints the tabs.
const LAYOUT_CLASSES = ["hidden-tab", "first-visible-tab", "last-visible-tab"];

// One full lap over every shown stage, then a second lap that stops on the target.
const cycleSequence = (visible, target) => {
  const stops = visible.includes(target) ? visible : [...visible, target].sort((a, b) => a - b);
  return [...stops, ...stops.slice(0, stops.indexOf(target) + 1)];
};

// Ease-out step lengths (the last hop is ~6x the first).
const cycleDelays = (count) => {
  const weights = Array.from({ length: count }, (_, step) => {
    const t = step / Math.max(1, count - 1);
    return 1 + 5 * t ** 3;
  });
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const totalMs = Math.min(CYCLE_MAX_MS, count * CYCLE_HOP_MS);
  return weights.map((weight) => (weight / total) * totalMs);
};

export const Stages = ({ stages, stageCase, classes = "" }) => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStageForModal, setSelectedStageForModal] = useState(null);
  const navRef = useRef(null);

  const handleStageClick = (stage) => {
    setSelectedStageForModal(stage);
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setSelectedStageForModal(null);
  };
  // const dynamicWidth = useSelector((state) => state.dynamicWidth.dynamicWidth);
  const selectedStage = stageCase?.auto_case_stage[0];
  const hasStageCase = stageCase && stageCase?.auto_case_stage?.length > 0;
  const caseID = getCaseId();
  const clientID = getClientId();
  const location = useLocation();

  // `current` lets the intro paint any stage as the active one with the same classes.
  const getStageClassName = (stage, current = selectedStage) => {
    if (!hasStageCase || !current)
      return "nav-item m-t-0 nav-link header-diagnoal font-weight-semibold new-hover-stages";

    if (stage.order < current.order) {
      return "nav-item m-t-0 nav-link header-diagnoal stage-links btn-primary-lighter text-grey new-hover-stages fw-normal past-case font-weight-semibold";
    }
    if (current.id === stage.id) {
      return "nav-item m-t-0 nav-link active font-weight-semibold new-hover-stages text-uppercase current-case z-index-9 header-diagnoal";
    }
    if (stage.order > current.order) {
      return "nav-item m-t-0 nav-link stage-links btn-primary-lighter new-hover-stages text-white fw-normal future-case header-diagnoal font-weight-semibold";
    }
    return "nav-item m-t-0 nav-link header-diagnoal font-weight-semibold new-hover-stages";
  };

  // Intro: the highlight cycles through the stages and comes to rest on the case's real stage.
  // Each step gives the visited tab the real active classes (and its neighbours the real past/
  // future ones), so it looks exactly like the selected stage. Classes are set on the DOM directly,
  // like the tab-fitting code does; the fitting pass is paused meanwhile so tabs don't hide and
  // reappear, and the original classes are put back at the end.
  const targetStageId = selectedStage?.id;
  // The page mounts hidden behind the "Opening Case" popup: wait until it closes so it's seen, and
  // replay on every case opening (going back to an earlier case included).
  const { loadingScreenVisible, caseOpenings } = useCaseOpening();
  const introKey = `${stageCase?.id ?? caseID}:${caseOpenings}`;
  const cyclingRef = useRef(false);
  // What React renders for each tab right now. Restoring from this (not a snapshot taken when the
  // spin started) stays correct even if the case's stage changes mid-spin.
  const realTabClassesRef = useRef([]);
  realTabClassesRef.current = (stages || []).map(
    (stage) => `${getStageClassName(stage)} stage-label cursor-pointer`
  );
  useEffect(() => {
    if (loadingScreenVisible || !hasStageCase || !stages || stages.length < 2) return;
    const target = stages.findIndex((stage) => stage.id === targetStageId);
    if (target < 0 || !shouldPlayIntro("case-stage", introKey)) return;
    const timers = [];
    let restore = null;
    const tabs = () => [...(navRef.current?.querySelectorAll("a.stage-label") || [])];
    const start = setTimeout(() => {
      const shown = tabs();
      const visible = stages
        .map((_, index) => index)
        .filter((index) => shown[index] && !shown[index].classList.contains("hidden-tab"));
      if (visible.length < 2) return;
      const layout = shown.map((tab) =>
        LAYOUT_CLASSES.filter((name) => tab.classList.contains(name)).join(" ")
      );
      const paint = (active) =>
        shown.forEach((tab, index) => {
          tab.className = [
            getStageClassName(stages[index], stages[active]),
            "stage-label cursor-pointer",
            layout[index],
          ]
            .filter(Boolean)
            .join(" ");
        });
      restore = () => {
        shown.forEach((tab, index) => {
          tab.className =
            [realTabClassesRef.current[index], layout[index]].filter(Boolean).join(" ") ||
            tab.className;
        });
        cyclingRef.current = false;
        restore = null;
        // Re-fit the tabs now that the real classes are back.
        requestAnimationFrame(() => setStageLabelsWidth());
      };
      cyclingRef.current = true;
      const sequence = cycleSequence(visible, target);
      // The last stop is the real stage: putting the original classes back lands on it.
      const steps = sequence.slice(0, -1);
      const delays = cycleDelays(steps.length);
      let elapsed = 0;
      steps.forEach((index, step) => {
        timers.push(setTimeout(() => paint(index), elapsed));
        elapsed += delays[step];
      });
      // Stops on the case's stage with no extra effect.
      timers.push(setTimeout(() => restore?.(), elapsed));
    }, CYCLE_START_DELAY_MS);
    return () => {
      clearTimeout(start);
      timers.forEach(clearTimeout);
      restore?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introKey, targetStageId, stages?.length, loadingScreenVisible]);

  const setStageLabelsWidth = () => {
    // The intro is repainting the tabs; it re-runs this once it has put the real classes back.
    if (cyclingRef.current) return;
    const container = document.querySelector(".top-nav-wrapper-inner");
    const stageContainer = document.querySelector(".stage-container");
    const icons = [...document.querySelectorAll(".inbox-icon img")];

    if (!container || !stageContainer) return;
    const containerWidth = container.getBoundingClientRect().width;
    const otherFixedWidth = containerWidth - 176 - 15;
    const availableWidth = containerWidth + 7 - 15;

    stageContainer.style.w = "auto";

    const stageElements = Array.from(stageContainer.children).filter((el) => el.tagName === "A");
    if (stageElements.length === 0) return;

    // Reset all tabs
    stageElements.forEach((el) => {
      el.classList.remove("hidden-tab", "last-visible-tab", "first-visible-tab");
      el.style.paddingRight = "";
      el.style.padding = "";
      el.style.marginLeft = "";
      // if (el.id === "padding-r-22" || el.id === "padding-l-22") {
      //   el.removeAttribute("id");
      // }
    });

    let totalWidth = 0;
    const currentIndex = stageElements.findIndex((el) => el.classList.contains("current-case"));
    const currentTab = currentIndex !== -1 ? stageElements[currentIndex] : null;
    const rightOfCurrent = currentTab?.nextElementSibling;

    // if (currentTab) {
    //   const prev = currentTab.previousElementSibling;
    //   const next = currentTab.nextElementSibling;

    //   if (prev?.classList.contains("stage-label")) {
    //     prev.id = "padding-r-22";
    //   }
    //   if (next?.classList.contains("stage-label")) {
    //     next.id = "padding-l-22";
    //   }
    // }

    for (let i = 0; i < stageElements.length; i++) {
      const el = stageElements[i];
      const elWidth = el.getBoundingClientRect().width;

      if (totalWidth + elWidth <= otherFixedWidth && icons.length === 2) {
        icons?.forEach((icon, index) => {
          icon.style.height = "41px";
          icon.nextElementSibling.style.marginTop = "0px";
          if (index === 0) {
            icon.parentElement.parentElement.parentElement.style.marginRight = "5px";
            icon.parentElement.parentElement.parentElement.style.left = "-10px";
          }
        });
      } else {
        icons?.forEach((icon, index) => {
          icon.style.height = "30px";
          icon.nextElementSibling.style.marginTop = "-2px";
          if (index === 0) icon.parentElement.parentElement.parentElement.style.left = "0px";
        });
      }

      if (totalWidth + elWidth > availableWidth) {
        const futureTabs = stageElements.filter(
          (el) => el.classList.contains("future-case") && !el.classList.contains("hidden-tab")
        );
        const pastTabs = stageElements.filter(
          (el) => el.classList.contains("past-case") && !el.classList.contains("hidden-tab")
        );

        const trimInOrder = [];

        const futureTabsExceptRight = futureTabs.filter((el) => el !== rightOfCurrent);

        trimInOrder.push(...futureTabsExceptRight.reverse());
        trimInOrder.push(...pastTabs);

        if (rightOfCurrent && !rightOfCurrent.classList.contains("hidden-tab")) {
          trimInOrder.push(rightOfCurrent);
        }

        if (currentTab) trimInOrder.push(currentTab);

        for (const el of trimInOrder) {
          if (el.classList.contains("hidden-tab")) continue;
          el.classList.add("hidden-tab");

          const visibleWidth = stageElements.reduce((acc, el) => {
            return el.classList.contains("hidden-tab")
              ? acc
              : acc + el.getBoundingClientRect().width;
          }, 0);

          if (visibleWidth <= availableWidth) break;
        }

        // if (rightOfCurrent && !rightOfCurrent.classList.contains("hidden-tab")) {
        //   const allOthersHidden = stageElements.every(
        //     (el) => el === rightOfCurrent || el.classList.contains("hidden-tab")
        //   );

        //   if (allOthersHidden && rightOfCurrent.id === "padding-l-22") {
        //     rightOfCurrent.removeAttribute("id");
        //   }
        // }

        break;
      } else {
        totalWidth += elWidth;
      }
    }

    // Mark the last visible tab
    for (let i = stageElements.length - 1; i >= 0; i--) {
      const el = stageElements[i];
      if (!el.classList.contains("hidden-tab")) {
        el.classList.add("last-visible-tab");
        break;
      }
    }

    // Mark the first visible tab
    const firstVisibleTab = stageElements.find((el) => !el.classList.contains("hidden-tab"));
    if (firstVisibleTab) {
      firstVisibleTab.classList.add("first-visible-tab");
      firstVisibleTab.style.marginLeft = window.innerWidth <= 992 ? "0px" : "0px";
    }
  };

  useEffect(() => {
    let rafId = 0;
    let t1 = 0,
      t2 = 0;
    const run = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setStageLabelsWidth();
      });
    };

    run();

    const rafInit = requestAnimationFrame(run);
    t1 = window.setTimeout(run, 0);
    t2 = window.setTimeout(run, 150);

    window.addEventListener("resize", run);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(run).catch(() => {
        // ignore fonts API errors
      });
    }

    const container = document.querySelector(".top-nav-wrapper-inner");
    const stageContainer = document.querySelector(".stage-container");
    const ro = new ResizeObserver(() => run());
    if (container) ro.observe(container);
    if (stageContainer) ro.observe(stageContainer);

    const mo = new MutationObserver(() => run());
    if (stageContainer) {
      mo.observe(stageContainer, { childList: true });
    }

    const imgs = Array.from(document.querySelectorAll(".inbox-icon img"));
    const imgHandlers = [];
    imgs.forEach((img) => {
      const onLoad = () => run();
      img.addEventListener("load", onLoad);
      imgHandlers.push([img, onLoad]);
    });

    window.addEventListener("load", run);

    return () => {
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(rafInit);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", run);
      window.removeEventListener("load", run);
      try {
        ro.disconnect();
      } catch (e) {
        /* pass */
      }
      try {
        mo.disconnect();
      } catch (e) {
        /* pass */
      }
      imgHandlers.forEach(([img, handler]) => {
        try {
          img.removeEventListener("load", handler);
        } catch (e) {
          /* pass */
        }
      });
    };
  }, [location.pathname, stages?.length, stageCase?.auto_case_stage?.[0]?.id, caseID, clientID]);

  return (
    <>
      <nav
        ref={navRef}
        className={`${classes ? classes : ""} stage-container flex-wrap nav nav-tabs d-block tabs-diagonal-reversed d-flex-lg-1 all-stages text-uppercase bg-nav-color font-weight-semibold bg-padding-remove`}
        // style={{ marginLeft: `${dynamicWidth - 310 || 0}px` }}
        id="nav-main"
      >
        {stages?.map((stage, index) => (
          <a
            key={index}
            className={`${getStageClassName(stage)} stage-label cursor-pointer`}
            onClick={() => handleStageClick(stage)}
            id="nav-link-hover-removal-z-index"
          >
            {stage.name}
          </a>
        ))}
        {/* <div className="more hidden" data-width="80">
        <a href="#">More</a>
        <div className="hidden-items" />
      </div> */}
      </nav>
      {showStatusModal && (
        <StatusModal
          show={showStatusModal}
          handleClose={handleCloseStatusModal}
          selectedStage={selectedStageForModal}
        />
      )}
    </>
  );
};
